import {AbstractControl, UntypedFormControl, ValidationErrors} from '@angular/forms';

import {DateUtils} from '@wba/utils/date.utils';
import {FormUtils} from '@wba/utils/form.utils';
import {MAX_CHAR_TEXT_AREA} from '@wba/commons/vars/global.const';
import {CurrencyUtils} from '@wba/utils/currency.utils';
import {UnitUtils} from '@wba/utils/unit.utils';

// textValidator erwartet andere Zeichenkettenlängen wie "Namen" mit dem nameValidator
const NAMES_MINLENGTH = 1;
const NAMES_MAXLENGTH = 30;
const DEFAULT_MAXLENGTH = 100;

export class StaticCommonValidators {

  static readonly LENGTH_ERROR = {
    typ: 'error',
    message: `Bitte mindestens ${NAMES_MINLENGTH} und maximal ${NAMES_MAXLENGTH} Zeichen eingeben.`
  };

  static readonly REQUIRED_ERROR = (label : string) => ({typ: 'error', message: label + ' ist ein Pflichtfeld.'});

  static wieOftProWocheValidator = () => {
    return (control: UntypedFormControl) => {
      if (control.value > 7) {
        return {
          typ: 'error',
          message: 'Eine Woche hat höchstens 7 Tage.'
        };
      }
      if (control.value < 1) {
        return {
          typ: 'error',
          message: 'Sie müssen mindestens 1 Tag angeben.'
        };
      }
      return null;
    };
  };

  static wieOftProMonatValidator = () => {
    return (control: UntypedFormControl) => {
      if (control.value > 31) {
        return {
          typ: 'error',
          message: 'Ein Monat hat höchstens 31 Tage.'
        };
      }
      if (control.value < 1) {
        return {
          typ: 'error',
          message: 'Sie müssen mindestens 1 Tag angeben.'
        };
      }
      return null;
    };
  };
  static required = (message = 'Ist ein Pflichtfeld.') => {
    return (control: UntypedFormControl) => control.value === null || control.value?.length === 0 ? {typ: 'error', message} : null;
  };

  static textValidator = (message: string, maxLength = DEFAULT_MAXLENGTH) => {
    if (maxLength == 0) {
      maxLength = DEFAULT_MAXLENGTH;
    }
    return (control: UntypedFormControl) => {
      if (FormUtils.nullOrEmpty(control.value) && message !== null) {   // Einziger Sonderfall bei dem message==null ist, "sonstigesWohnverhaeltnis" in Umzug
        return {typ: 'error', message: message};
      }
      if (control.value && control.value.trim().length > maxLength) {
        return {typ: 'error', message: 'Bitte geben Sie maximal ' + maxLength + ' Zeichen ein.'};
      }
      return null;
    };
  };

  static selectValidator = (label: string) => {
    const error: ValidationErrors = {typ: 'error', message: label};
    return (control: UntypedFormControl) => FormUtils.nullOrEmpty(control.value) || control.value === 0 ? error : null;
  };

  /**
   * valid inputs:
   * - null
   * - ''
   * - 12.12.2019
   */
  static dateOptionalValidator = () => {
    const fehler: ValidationErrors = {
      typ: 'error',
      message: 'Bitte geben Sie das Datum im korrekten Format ein: TT.MM.JJJJ zum Beispiel: ' + DateUtils.today()
    };

    return (control: AbstractControl) => (!FormUtils.nullOrEmpty(control.value) && DateUtils.notValid(control.value)) ? fehler : null;
  };

  /**
   * valid inputs:
   * - 12.12.2019
   *
   * @param nullOrEmptyLabel (optional) zB: `Tag der Änderung ist ein Pflichtfeld`
   * @returns a curried function
   */
  static dateValidator(nullOrEmptyLabel?) {
    const fehler: ValidationErrors = {
      typ: 'error',
      message: 'Bitte geben Sie das Datum im korrekten Format ein: TT.MM.JJJJ zum Beispiel: ' + DateUtils.today()
    };

    return (control: AbstractControl) => {
      if (FormUtils.nullOrEmpty(control.value)) {
        return nullOrEmptyLabel
          ? {...fehler, message: nullOrEmptyLabel}
          : fehler;

      } else if (DateUtils.notValid(control.value)) {
        return fehler;

      } else {
        return null; // => no error
      }
    };
  }

  static birthdayValidator = (control: AbstractControl) => {
    const notValid = control.value === null || control.value === '' || DateUtils.notValid(control.value);

    if (notValid) {
      return {
        typ: 'error',
        message: `Bitte geben Sie das Datum im korrekten Format ein: TT.MM.JJJJ zum Beispiel: ${DateUtils.today()}`
      };
    }

    return DateUtils.isInFuture(control.value) ? {
      typ: 'error',
      message: 'Bitte geben Sie ein gültiges Geburtsdatum ein.'
    } : null;
  };

  static requiredLengthValidator = (label: string, minLength = NAMES_MINLENGTH, maxLength: number = NAMES_MAXLENGTH) => (control: AbstractControl) => {

    if (control.value === null) {
      return StaticCommonValidators.REQUIRED_ERROR(label);
    }

    const trimmed = control.value.trim().length
    if (trimmed < minLength || trimmed > maxLength) {
      if (minLength == 0) {
        return {
          typ: 'error',
          message: `Bitte geben Sie maximal ${maxLength} Zeichen ein.`
        };
      } else {
        return {
          typ: 'error',
          message: `Bitte mindestens ${minLength} und maximal ${maxLength} Zeichen eingeben.`
        };
      }
    }
    return null;
  };

  static ibanValidator = (label: string = "IBAN", minLength = 1, maxLength: number = 50) => (control: AbstractControl) => {

    if (control.value === null) {
      return StaticCommonValidators.REQUIRED_ERROR(label);
    }

    const trimmed = control.value.trim().length
    if (trimmed < minLength || trimmed > maxLength) {
      return {
        typ: 'error',
        message: " Geben Sie im folgenden Feld die IBAN mit Leerstellen ein, z.B. DExx xxxx xxxx xxxx xxxx xx. Es sind maximal 50 Zeichen erlaubt."
      };
    }
    return null;
  };

  static textAreaValidator = (control: AbstractControl) => {
    if (!control.value) {
      return null;
    }

    return control.value.length > MAX_CHAR_TEXT_AREA ? {
      typ: 'error',
      message: `Sie haben die Anzahl an zulässigen Zeichen überschritten. Bitte kürzen Sie Ihren Text auf maximal ${MAX_CHAR_TEXT_AREA} Zeichen.`
    } : null;
  };

  static minValueValidator = (control: AbstractControl | { value: string }) => {
    return (
      CurrencyUtils.isEmptyOrNaN(control.value)
      || Number.parseFloat(control.value.replace(/[.]/g, '').replace(/','/g, '.')) < 1.0
    ) ? {
      typ: 'error',
      message: 'Bitte geben Sie einen Betrag an, der mindestens dem Wert 1,00 entspricht.'
    } : null;
  };

  static minYearValidator = (control: AbstractControl | { value: string }) => {
    return (
      control.value &&
      UnitUtils.convertLongFromService(control.value)
      && (control.value < 1400 || control.value > 2099)
    ) ? {
      typ: 'error',
      message: 'Bitte geben Sie eine gültige Jahreszahl an: 2020 zum Beispiel.'
    } : null;
  };
}
